import { Grid } from "@material-ui/core";
import { useEffect, useState, useRef, useMemo } from "react";
import { Condominio } from "modules/groupPay/configuracaoGroupPay/ModalReplicarAcesso";
import TabelaConfigurarCondominios from "./TabelaConfigurarCondominios";
import { OpcoesSelecionaveisCheckBox } from "types/OpcoesSelecionaveisCheckBoxType";
import { Box, Link } from "@mui/material";
import { FaYoutube } from "react-icons/fa";
import { useStyles } from "./configurarBuscaNFEmail/styles/ConfigurarBuscaNFEmailStyles";
import StandardModalBemVindoPay from "shared/components/standardModal/StandardModalBemVindoPay";

type AtivacaoCondominiosProps = {
  condominios: any;
  searchCondominio: string;
  setSearchCondominio: React.Dispatch<React.SetStateAction<string>>;
  condominiosSelecionados: Condominio[];
  setCondominiosSelecionados: React.Dispatch<React.SetStateAction<Condominio[]>>;
  idsCondominiosSelecionados: number[];
  setIdsCondominiosSelecionados: React.Dispatch<React.SetStateAction<number[]>>;
  setSortString: (value: string) => void;
  isFetching: boolean;
  register: any;
  setValue: any;
  getValues: any;
};

const AtivacaoCondominios: React.FC<AtivacaoCondominiosProps> = ({
  condominios,
  searchCondominio,
  setSearchCondominio,
  condominiosSelecionados,
  setCondominiosSelecionados,
  idsCondominiosSelecionados,
  setIdsCondominiosSelecionados,
  register,
  setSortString,
}) => {
  const [isCheckBoxChecked, setIsCheckBoxChecked] = useState<boolean>(false);
  const [showModalAjudaAtivacaoCondominio, setShowModalAjudaAtivacaoCondominio] = useState(false);
  const initializedRef = useRef(false);
  const [condominiosFiltrados, setCondominiosFiltrados] = useState<Condominio[]>([]);
  const classes = useStyles();

  useEffect(() => {
    register("condominiosIds", { required: "Selecione pelo menos um condomínio." });
  }, [register]);

  useEffect(() => {
    if (condominios?.content?.length > 0) {
      if (idsCondominiosSelecionados.length === 0 && !initializedRef.current) {
        initializedRef.current = true;

        const condominiosAtivos = condominios.content.filter((cond: Condominio) => cond.possuiGroupPayAtivo);

        setCondominiosSelecionados(condominiosAtivos);
        setIdsCondominiosSelecionados(condominiosAtivos.map((cond: Condominio) => cond.condominioId));
      } else if (idsCondominiosSelecionados.length > 0) {
        const condominiosSincronizados = condominios.content.filter((cond: Condominio) =>
          idsCondominiosSelecionados.includes(cond.condominioId)
        );

        if (condominiosSincronizados.length !== condominiosSelecionados.length) {
          setCondominiosSelecionados(condominiosSincronizados);
          const idsExistentes = condominiosSincronizados.map((cond: Condominio) => cond.condominioId);
          setIdsCondominiosSelecionados(idsExistentes);
        }
      }
    }
  }, [
    condominios,
    idsCondominiosSelecionados,
    condominiosSelecionados.length,
    setCondominiosSelecionados,
    setIdsCondominiosSelecionados,
  ]);

  useEffect(() => {
    const totalCondominios = condominios?.content?.length || 0;
    const totalSelecionados = condominiosSelecionados.length;

    setIsCheckBoxChecked(totalCondominios > 0 && totalSelecionados === totalCondominios);
  }, [condominiosSelecionados, condominios]);

  const condominiosSelecionadosFiltrados = useMemo(() => {
    return idsCondominiosSelecionados.filter((id) =>
      condominiosFiltrados.some((cond) => cond.condominioId === id)
    );
  }, [idsCondominiosSelecionados, condominiosFiltrados]);

  const handleSelecionarCondominio = (condominio: Condominio) => {
    setCondominiosSelecionados((prev) => {
      if (prev.some((item) => item.condominioId === condominio.condominioId)) {
        return prev;
      }
      return [...prev, condominio];
    });

    setIdsCondominiosSelecionados((prev) => {
      if (prev.includes(condominio.condominioId)) {
        return prev;
      }
      return [...prev, condominio.condominioId];
    });
  };

  const handleDesmarcarCondominio = (condominio: Condominio) => {
    setCondominiosSelecionados((prev) => prev.filter((item) => item.condominioId !== condominio.condominioId));

    setIdsCondominiosSelecionados((prev) => prev.filter((id) => id !== condominio.condominioId));
  };

  const handleMarcarEDesmarcarCondominio = (condominio: Condominio) => {
    const isSelected = idsCondominiosSelecionados.includes(condominio.condominioId);

    if (isSelected) {
      handleDesmarcarCondominio(condominio);
    } else {
      handleSelecionarCondominio(condominio);
    }
  };

  const handleSelecionarTodos = (opcao: OpcoesSelecionaveisCheckBox, condominiosParaSelecionar?: Condominio[]) => {
    if (opcao === "TODAS_PAGINA") {
      const todosCondominios = condominiosParaSelecionar ?? condominios?.content ?? [];
      setCondominiosSelecionados(todosCondominios);
      setIdsCondominiosSelecionados(todosCondominios.map((cond: Condominio) => cond.condominioId));
    } else {
      setCondominiosSelecionados([]);
      setIdsCondominiosSelecionados([]);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ marginLeft: "5rem" }}>
          <Box className={classes.header}>
            <Box className={classes.titleContainer}>
              <Box className={classes.titleWrapper}>
                <h2 className={classes.wizardTitle}>1. Ativação dos condomínios</h2>
                <span className={classes.headerPipe}>|</span>
                <Box className={classes.headerLinkContainer}>
                  <Link onClick={() => setShowModalAjudaAtivacaoCondominio(true)} className={classes.headerLink}>
                    <FaYoutube size={18} />
                    Ver sobre o Group Pay
                  </Link>
                </Box>
              </Box>
              <p className={classes.subtitle}>Selecione abaixo os condomínios que irão utilizar o Group Pay.</p>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TabelaConfigurarCondominios
            condominios={condominios?.content ?? []}
            searchCondominio={searchCondominio}
            setSearchCondominio={setSearchCondominio}
            sizeInputSearch={7}
            renderCheckbox={true}
            onSelecionarCondominio={handleMarcarEDesmarcarCondominio}
            idsSelecionados={idsCondominiosSelecionados}
            handleSelecionarTodos={handleSelecionarTodos}
            isChecked={isCheckBoxChecked}
            centerTable={true}
            tableSize={6}
            renderGrupoCondominios={true}
            setSortString={setSortString}
            onCondominiosFiltradosChange={setCondominiosFiltrados}
          />
        </Grid>
        {condominiosSelecionadosFiltrados?.length > 0 ? (
          condominiosSelecionadosFiltrados?.length === 1 ? (
            <Grid item xs={12} style={{ marginLeft: "5rem" }}>
              <span className={classes.textoCondominioSelecionados}>
                {condominiosSelecionadosFiltrados.length} condomínio selecionado
              </span>
            </Grid>
          ) : (
          <Grid item xs={12} style={{ marginLeft: "5rem" }}>
            <span className={classes.textoCondominioSelecionados}>
              {condominiosSelecionadosFiltrados.length} condomínios selecionados
            </span>
          </Grid>
          )) : null}
      </Grid>

      <StandardModalBemVindoPay
        showModal={showModalAjudaAtivacaoCondominio}
        onCloseModal={() => setShowModalAjudaAtivacaoCondominio(false)}
      />
    </>
  );
};

export default AtivacaoCondominios;
